import {ACTToolbarService} from 'src/app/angularJS/services/toolbar.service';
import {BreadcrumbService} from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import {FulfilmentRequestsService} from 'src/app/angularJS/services/fulfilment/fulfilment.service';
import {QueryBuilder} from 'src/app/angularJS/services/query-builder.service';
import {UserMessagesService} from 'src/app/angularJS/services/user-messages.service';
import FulfilmentRequest = InterACT.Interfaces.FulfilmentRequests.FulfilmentRequest;

/**
 * A controller that shows a tabluar view for Fullfilment Requests by status
 */
export class FulfilmentRequestsController {
  public static $inject = [
    '$rootScope',
    'ACTToolbarService',
    'breadcrumbService',
    '$stateParams',
    'fulfilmentRequestsService',
    'QueryBuilder',
    '$state',
    'UserMessages'
  ];

  public fulfilmentRequests: FulfilmentRequest[];
  public paginatorData: InterACT.Interfaces.IPaginationData;
  public searchQuery: string;
  public fulfilmentProgressStates: string[];

  constructor(
    private $rootScope: ng.IRootScopeService,
    private toolbarService: ACTToolbarService,
    private breadcrumbService: BreadcrumbService,
    private $stateParams: ng.ui.IStateParamsService,
    private fulfilmentRequestsService: FulfilmentRequestsService,
    private queryBuilderService: QueryBuilder,
    private $state: ng.ui.IStateService,
    private userMessages: UserMessagesService) {

    this.activate();
  }

  /**
   * Callback that gets passed to the search component and returns a constructed query string
   *
   * @param searchQuery The constrcuted query string
   */
  public onPerformSearch = (searchQuery: string) => {
    if (_.includes(searchQuery, 'undefined') || _.includes(searchQuery, "''")) {
      this.searchQuery = 'sortBy timestamp desc';
    } else {
      this.searchQuery = searchQuery;
    }

    this.getFulfilmentRequests(undefined, undefined, this.searchQuery);
  }

  /**
   * Handles the retrieval of Fulfilment Requests and gets invoked by the paginator component
   *
   * @param page The new current page
   * @param perPage The new per page count
   * @param A search query
   *
   * @returns A http promise used by the paginator component
   */
  public getFulfilmentRequests = (page: number, perPage: number, searchQuery: string) => {
    let fieldName = '';
    let query = this.queryBuilderService.newParam();
    let qParam;
    let {queryName, filterQuery} = this.$stateParams;

    if (queryName && queryName !== '') {
      if (_.includes(queryName, 'catalogues')) {
        fieldName = 'actions.variantExternalId';
      } else {
        fieldName = 'state.name';
      }
    }

    this.$rootScope.showSpinner = true;
    this.setPageData(page, perPage);

    if (fieldName !== '' && queryName) {
      query.fieldIs(fieldName, queryName)
    }

    if (filterQuery) {
      query.and().isGreaterOrEqualTo('state.timestamp', filterQuery)
    }

    query.sortBy([
      {columnName: 'timestamp', direction: 'desc'}
    ]);

    if (searchQuery) {
      qParam = searchQuery;
    } else if (query) {
      qParam = query.getParam();
    } else {
      qParam = undefined;
    }

    return this.fulfilmentRequestsService.getFulfilmentRequests(
      this.paginatorData.currentPage,
      this.paginatorData.perPageCount,
      qParam
    ).then(response => {
      this.paginatorData.totalCount = response.meta.totalCount;
      this.fulfilmentRequests = response.fulfilmentRequests;
      this.$rootScope.showSpinner = false;
    });
  }

  /**
   * Changes the state to the Fulfilment Request Summary view
   *
   * @param fulfilmentRequest The fulfilment Request object we wish to view in summary tab
   */
  public goToFulfilmentSummary = (fulfilmentRequest: InterACT.Interfaces.FulfilmentRequests.FulfilmentRequest) => {
    this.$state.go('fulfilment-request.summary', {
      externalId: fulfilmentRequest.externalId
    });
  }

  /**
   * Checks if a given Fulfilment Request is in progress
   * @param fulfilmentRequest the fulfilment that you want to check the status of
   * @returns true if a Fulfilment Request is in progress
   */
  public fulfilmentRequestIsInProgress = (fulfilmentRequest: FulfilmentRequest) => {
    return this.fulfilmentProgressStates.indexOf(fulfilmentRequest.state.name) !== -1;
  }

  /**
   * Cancels a Fulfilment Request
   *
   * @param fulfilmentRequest The Fulfilment Request to cancel
   *
   * @returns a promise when an option has been selected from the confirm dialog component
   */
  public cancelFulfilmentRequest = (fulfilmentRequest: FulfilmentRequest) => {
    return this.userMessages.buildConfirmDialog(
      'CONFIRM_DIALOG.FULFILMENT.CANCEL',
      'CONFIRM_DIALOG.FULFILMENT.CANCEL_MESSAGE',
      undefined,
      'CONFIRM_DIALOG.FULFILMENT.DELETE_CONFIRM'
    ).then(() => {
      this.fulfilmentRequestsService.cancelFulfilmentRequest(fulfilmentRequest).then(() => {
        this.userMessages.showToast('TOAST_MESSAGE.FULFILMENT.CANCELLED');
        setTimeout(() => {
          this.getFulfilmentRequests(this.paginatorData.currentPage, this.paginatorData.perPageCount, this.searchQuery);
        }, 1000);
      });
    });
  }

  /**
   * This function is used to set the paginator data for requests
   *
   * @param page the new current page
   * @param perPage the new per page count
   */
  private setPageData = (page?: number, perPage?: number) => {
    if (!_.isUndefined(page)) {
      this.paginatorData.currentPage = angular.isNumber(page) ? page : 1;
    }

    this.paginatorData.perPageCount = angular.isNumber(perPage) ? perPage : this.paginatorData.perPageCount;
  }

  /**
   * Get Fulfilment's Request State
   *
   */
  private getState(fulfilmentRequest: FulfilmentRequest) {
    return this.fulfilmentRequestsService.getFulfilmentRequestState(fulfilmentRequest);
  }

  /**
   * This function is used to get the Action/s Type of a fulfilment request
   *
   * @param fulfilmentRequest the fulfilment request
   */
  public fulfilmentActionsType = (fulfilmentRequest: FulfilmentRequest) => {
    return [...new Set(fulfilmentRequest.actions.map(function (c) {
      return c.type
    }))].join(" / ");
  }

  /**
   * Gets invoked upon instantiation - Sets up default settings for the controller
   */
  private activate = () => {
    this.toolbarService.getToolbar('primary').then((toolbar: any) => {
      toolbar.setTitle('FULFILMENT_REQUESTS');
    });

    this.paginatorData = {
      perPageCount: 15,
      currentPage: 1,
      totalCount: 0
    }

    this.breadcrumbService.setBreadcrumbs([{
      name: 'FULFILMENT',
      sref: 'fulfilment'
    }, {
      name: 'FULFILMENT_REQUESTS',
      sref: 'fulfilment-requests'
    }]);

    this.fulfilmentProgressStates = [
      "FULFILMENT_AWAITING_DISTRIBUTION",
      "FULFILMENT_REJECTED",
      "FULFILMENT_HELD",
      "FULFILMENT_DISTRIBUTED",
      "FULFILMENT_ACCEPTED",
      "FULFILMENT_CONSTRUCTED"
    ];
  }
}
